import { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { ChevronLeft, ChevronRight, Pencil, Plus, Trash2, X } from "lucide-react";
import { z } from "zod";
import { DateTime } from "luxon";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Form, FormField, FormControl, FormMessage, FormItem } from "@/components/ui/form";
import { useFormHandler } from "@/hooks/useFormHandler";
import { useStore } from "@/hooks/useStore";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import {
	structureProperty,
	type StructureProperty,
	type StructurePropertyHistoryRecord,
} from "@/endpoints/dryp/structureProperty";

const OPEN_SENTINEL = "";
const SORT_INFINITY = "9999-12-31";

type EntryFormValue = {
	startsAt: string;
	endsAt: string;
	value: number;
};

type PropertyFormValues = {
	entries: EntryFormValue[];
};

const dateOrEmpty = z
	.string()
	.refine(text => text === "" || /^\d{4}-\d{2}-\d{2}$/.test(text), { message: "Use YYYY-MM-DD" });

const buildSchema = () =>
	z
		.object({
			entries: z.array(
				z
					.object({
						startsAt: dateOrEmpty,
						endsAt: dateOrEmpty,
						value: z.coerce.number({ invalid_type_error: "Enter a number" }),
					})
					.refine(entry => entry.startsAt === "" || entry.endsAt === "" || entry.startsAt <= entry.endsAt, {
						message: "Start must be on or before end",
						path: ["endsAt"],
					}),
			),
		})
		.superRefine((formValues, refinementContext) => {
			let openEndedCount = 0;
			let firstOpenEndedIndex = -1;
			for (let entryIndex = 0; entryIndex < formValues.entries.length; entryIndex++) {
				if (formValues.entries[entryIndex].endsAt === "") {
					openEndedCount++;
					if (firstOpenEndedIndex === -1) firstOpenEndedIndex = entryIndex;
				}
			}
			if (openEndedCount > 1) {
				for (let entryIndex = 0; entryIndex < formValues.entries.length; entryIndex++) {
					if (formValues.entries[entryIndex].endsAt === "" && entryIndex !== firstOpenEndedIndex) {
						refinementContext.addIssue({
							code: z.ZodIssueCode.custom,
							message: "Only one entry can extend to ∞",
							path: ["entries", entryIndex, "endsAt"],
						});
					}
				}
			}

			for (let outerIndex = 0; outerIndex < formValues.entries.length; outerIndex++) {
				for (let innerIndex = outerIndex + 1; innerIndex < formValues.entries.length; innerIndex++) {
					const firstEntry = formValues.entries[outerIndex];
					const secondEntry = formValues.entries[innerIndex];
					const firstStart = firstEntry.startsAt || "0000-00-00";
					const secondStart = secondEntry.startsAt || "0000-00-00";
					const firstEnd = firstEntry.endsAt || SORT_INFINITY;
					const secondEnd = secondEntry.endsAt || SORT_INFINITY;
					if (firstStart <= secondEnd && secondStart <= firstEnd) {
						refinementContext.addIssue({
							code: z.ZodIssueCode.custom,
							message: "This range overlaps another entry",
							path: ["entries", innerIndex, "endsAt"],
						});
					}
				}
			}
		});

const toDateInputValue = (isoOrNull: string | null): string => {
	if (!isoOrNull) return "";
	const parsed = DateTime.fromISO(isoOrNull);
	return parsed.isValid ? parsed.toFormat("yyyy-MM-dd") : "";
};

const toIsoOrNull = (dateInputValue: string): string | null => {
	if (!dateInputValue) return null;
	const parsed = DateTime.fromFormat(dateInputValue, "yyyy-MM-dd", { zone: "utc" });
	return parsed.isValid ? parsed.toISO() : null;
};

const splitHistoryIntoFormValues = (property: StructureProperty): PropertyFormValues => ({
	entries: property.history.map(record => ({
		startsAt: toDateInputValue(record.startsAt),
		endsAt: toDateInputValue(record.endsAt),
		value: record.value,
	})),
});

const mergeFormValuesIntoHistory = (formValues: PropertyFormValues): StructurePropertyHistoryRecord[] =>
	formValues.entries.map(entry => ({
		startsAt: toIsoOrNull(entry.startsAt),
		endsAt: toIsoOrNull(entry.endsAt),
		value: entry.value,
	}));

const findCurrentEntry = (entries: EntryFormValue[] | undefined): EntryFormValue | undefined =>
	(entries ?? []).find(entry => entry.endsAt === OPEN_SENTINEL);

const sortKeyForEndsAt = (endsAt: string): string => endsAt || SORT_INFINITY;

type OpenDateFieldProps = {
	fieldName: `entries.${number}.startsAt` | `entries.${number}.endsAt`;
	label: string;
	openLabel: string;
};

const OpenDateField = ({ fieldName, label, openLabel }: OpenDateFieldProps) => {
	const { control } = useFormContext<PropertyFormValues>();
	return (
		<FormField
			control={control}
			name={fieldName}
			render={({ field }) => {
				const isOpen = field.value === OPEN_SENTINEL;
				return (
					<FormItem className="space-y-1.5">
						<Label className="text-xs text-muted-foreground">{label}</Label>
						<FormControl>
							<Input
								type="date"
								value={field.value || ""}
								onChange={field.onChange}
								onBlur={field.onBlur}
								disabled={isOpen}
							/>
						</FormControl>
						<label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
							<Checkbox
								checked={isOpen}
								onCheckedChange={checked => {
									if (checked) {
										field.onChange(OPEN_SENTINEL);
									} else {
										field.onChange(toDateInputValue(new Date().toISOString()));
									}
								}}
							/>
							{openLabel}
						</label>
						<FormMessage />
					</FormItem>
				);
			}}
		/>
	);
};

type EntryRowProps = {
	rowIndex: number;
	unit: string;
	onRemove: () => void;
};

const EntryRow = ({ rowIndex, unit, onRemove }: EntryRowProps) => {
	const [isEditing, setIsEditing] = useState(false);
	const { control, getValues } = useFormContext<PropertyFormValues>();
	const entry = useWatch({ control, name: `entries.${rowIndex}` }) ?? getValues(`entries.${rowIndex}`);
	const isCurrent = entry?.endsAt === OPEN_SENTINEL;

	if (isEditing) {
		return (
			<div
				className={
					isCurrent
						? "rounded-md border-2 border-primary/40 bg-card p-3 space-y-3"
						: "rounded-md border bg-card p-3 space-y-3"
				}
			>
				{isCurrent && (
					<span className="inline-flex items-center rounded-full bg-primary/15 text-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
						Current
					</span>
				)}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
					<OpenDateField
						fieldName={`entries.${rowIndex}.startsAt`}
						label="Starts at"
						openLabel="Since beginning"
					/>
					<OpenDateField
						fieldName={`entries.${rowIndex}.endsAt`}
						label="Ends at"
						openLabel="Until now (current)"
					/>
				</div>
				<FormField
					control={control}
					name={`entries.${rowIndex}.value`}
					render={({ field }) => (
						<FormItem className="space-y-1.5">
							<Label className="text-xs text-muted-foreground">Value{unit ? ` (${unit})` : ""}</Label>
							<FormControl>
								<Input type="number" step="any" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<div className="flex justify-end">
					<Button type="button" size="sm" variant="ghost" onClick={() => setIsEditing(false)}>
						Done
					</Button>
				</div>
			</div>
		);
	}

	const startLabel = entry?.startsAt || "since beginning";
	const endLabel = entry?.endsAt || "now";
	const rangeLabel = `${startLabel} → ${endLabel}`;
	const valueLabel = `${entry?.value ?? "—"}${unit ? ` ${unit}` : ""}`;

	return (
		<div
			className={
				isCurrent
					? "flex items-center justify-between rounded-md border-2 border-primary/30 bg-primary/5 px-3 py-2 text-sm"
					: "flex items-center justify-between rounded-md border bg-card px-3 py-2 text-sm"
			}
		>
			<div className="flex items-center gap-3 min-w-0">
				<span
					className={
						isCurrent
							? "h-2 w-2 rounded-full bg-primary shrink-0"
							: "h-2 w-2 rounded-full bg-muted-foreground/40 shrink-0"
					}
				/>
				<span className={isCurrent ? "font-semibold tabular-nums" : "font-medium tabular-nums"}>
					{valueLabel}
				</span>
				<span className="text-xs text-muted-foreground tabular-nums truncate">{rangeLabel}</span>
				{isCurrent && (
					<span className="inline-flex items-center rounded-full bg-primary/15 text-primary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide shrink-0">
						Current
					</span>
				)}
			</div>
			<div className="flex items-center gap-1 shrink-0">
				<Button type="button" size="icon" variant="ghost" onClick={() => setIsEditing(true)}>
					<Pencil className="h-3.5 w-3.5" />
				</Button>
				<Button type="button" size="icon" variant="ghost" onClick={onRemove}>
					<Trash2 className="h-3.5 w-3.5 text-destructive" />
				</Button>
			</div>
		</div>
	);
};

type PropertyHistoryChartProps = {
	entries: EntryFormValue[];
	unit: string;
};

const PropertyHistoryChart = ({ entries, unit }: PropertyHistoryChartProps) => {
	const chartOptions = useMemo<Highcharts.Options>(() => {
		const sortedEntries = [...entries].sort((firstEntry, secondEntry) =>
			sortKeyForEndsAt(firstEntry.endsAt).localeCompare(sortKeyForEndsAt(secondEntry.endsAt)),
		);

		const earliestExplicitStart = sortedEntries.find(entry => entry.startsAt)?.startsAt;
		const fallbackStartMillis = earliestExplicitStart
			? DateTime.fromFormat(earliestExplicitStart, "yyyy-MM-dd").minus({ months: 6 }).toMillis()
			: DateTime.now().minus({ years: 3 }).toMillis();

		const dataPoints: Highcharts.PointOptionsObject[] = sortedEntries.map(entry => {
			const startMillis = entry.startsAt
				? DateTime.fromFormat(entry.startsAt, "yyyy-MM-dd").toMillis()
				: fallbackStartMillis;
			const isCurrent = entry.endsAt === OPEN_SENTINEL;
			return {
				x: startMillis,
				y: entry.value,
				marker: isCurrent
					? { radius: 6, fillColor: "#2563eb", lineColor: "#ffffff", lineWidth: 2 }
					: { radius: 3, fillColor: "#94a3b8" },
			};
		});

		const currentEntry = sortedEntries.find(entry => entry.endsAt === OPEN_SENTINEL);
		if (currentEntry) {
			dataPoints.push({
				x: DateTime.now().toMillis(),
				y: currentEntry.value,
				marker: { radius: 6, fillColor: "#2563eb", lineColor: "#ffffff", lineWidth: 2 },
			});
		}

		return {
			chart: { type: "line", height: 180, backgroundColor: "transparent" },
			title: { text: undefined },
			credits: { enabled: false },
			legend: { enabled: false },
			xAxis: { type: "datetime", labels: { style: { fontSize: "11px" } } },
			yAxis: {
				title: { text: unit || undefined, style: { fontSize: "11px" } },
				labels: { style: { fontSize: "11px" } },
			},
			tooltip: {
				xDateFormat: "%Y-%m-%d",
				pointFormat: `<b>{point.y}${unit ? " " + unit : ""}</b><br/>`,
			},
			plotOptions: {
				series: {
					step: "left",
					marker: { enabled: true },
					animation: false,
				},
			},
			series: [
				{
					type: "line",
					name: "Value",
					data: dataPoints,
					color: "#94a3b8",
				},
			],
		};
	}, [entries, unit]);

	return (
		<div className="rounded-md border bg-muted/20 p-2">
			<HighchartsReact highcharts={Highcharts} options={chartOptions} />
		</div>
	);
};

interface EditStructurePropertyModalProps {
	property: StructureProperty;
	onSuccess?: () => void;
}

const EditStructurePropertyModalComponent = ({ property, onSuccess }: EditStructurePropertyModalProps) => {
	const {
		uiStore: { modalStore },
	} = useStore();
	const updateApi = useAsyncStateStore(structureProperty.update);
	const [historyExpanded, setHistoryExpanded] = useState(false);

	const schema = useMemo(() => buildSchema(), []);
	const defaultValues = useMemo(() => splitHistoryIntoFormValues(property), [property]);

	const { form, handleFormErrors } = useFormHandler<PropertyFormValues>(schema, { defaultValues });

	const entriesArray = useFieldArray({ control: form.control, name: "entries" });
	const watchedEntries = form.watch("entries");

	const entriesCount = entriesArray.fields.length;
	const showChart = entriesCount >= 2;
	const currentEntry = findCurrentEntry(watchedEntries);

	const endsAtSignature = (watchedEntries ?? []).map(entry => entry.endsAt ?? "").join("|");
	const sortedFieldIndices = useMemo(() => {
		return entriesArray.fields
			.map((_, fieldIndex) => fieldIndex)
			.sort((firstIndex, secondIndex) => {
				const firstKey = sortKeyForEndsAt(watchedEntries?.[firstIndex]?.endsAt ?? "");
				const secondKey = sortKeyForEndsAt(watchedEntries?.[secondIndex]?.endsAt ?? "");
				return secondKey.localeCompare(firstKey);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entriesCount, endsAtSignature]);

	const onSubmit = async (values: PropertyFormValues) => {
		try {
			await updateApi.fetch(property.structureId, property.id, {
				history: mergeFormValuesIntoHistory(values),
			});
			onSuccess?.();
			modalStore.close();
		} catch (error) {
			handleFormErrors(error);
		}
	};

	const handleAddEntry = () => {
		const hasCurrent = !!currentEntry;
		entriesArray.prepend({
			startsAt: "",
			endsAt: hasCurrent ? toDateInputValue(new Date().toISOString()) : "",
			value: 0,
		});
		setHistoryExpanded(true);
	};

	const historyToggleLabel = historyExpanded
		? "Hide history"
		: entriesCount > 0
			? `Edit history (${entriesCount})`
			: "Edit history";

	return (
		<>
			<DialogHeader>
				<DialogTitle>
					Edit {property.name}
					{property.unit ? <span className="text-muted-foreground"> ({property.unit})</span> : null}
				</DialogTitle>
				<DialogDescription className="sr-only">
					Edit the history entries for this structure property. The entry that extends to ∞ is the current
					value.
				</DialogDescription>
			</DialogHeader>

			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
					<div className="flex flex-col md:flex-row gap-6 items-stretch">
						<section className="w-full md:flex-1 md:basis-0 min-w-0 space-y-3">
							<h3 className="text-sm font-medium text-muted-foreground">Current value</h3>
							<div className="rounded-lg border bg-muted/30 px-4 py-3">
								<div className="text-2xl font-semibold tabular-nums">
									{currentEntry && Number.isFinite(Number(currentEntry.value))
										? `${currentEntry.value}${property.unit ? ` ${property.unit}` : ""}`
										: "—"}
								</div>
								<div className="text-xs text-muted-foreground mt-0.5">
									{!currentEntry
										? "No current value set"
										: currentEntry.startsAt
											? `Since ${currentEntry.startsAt}`
											: "Since beginning"}
								</div>
							</div>

							{showChart && <PropertyHistoryChart entries={watchedEntries ?? []} unit={property.unit} />}

							<div className="flex justify-end">
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => setHistoryExpanded(previous => !previous)}
									className="text-muted-foreground hover:text-foreground gap-1"
								>
									<span>{historyToggleLabel}</span>
									{historyExpanded ? (
										<ChevronLeft className="h-3.5 w-3.5" />
									) : (
										<ChevronRight className="h-3.5 w-3.5" />
									)}
								</Button>
							</div>
						</section>

						{historyExpanded && (
							<section className="w-full md:flex-1 md:basis-0 min-w-0 space-y-3 md:border-l md:pl-6">
								<div className="flex items-center justify-between">
									<h3 className="text-sm font-medium text-muted-foreground">Edit history</h3>
									<Button type="button" variant="outline" size="sm" onClick={handleAddEntry}>
										<Plus className="h-3.5 w-3.5 mr-1" />
										Add entry
									</Button>
								</div>

								<div className="space-y-2 max-h-[28rem] overflow-y-auto pr-1">
									{entriesCount === 0 ? (
										<p className="text-xs text-muted-foreground italic pt-1">
											No entries yet. Click "Add entry" to add the current value.
										</p>
									) : (
										sortedFieldIndices.map(fieldIndex => {
											const fieldItem = entriesArray.fields[fieldIndex];
											return (
												<EntryRow
													key={fieldItem.id}
													rowIndex={fieldIndex}
													unit={property.unit}
													onRemove={() => entriesArray.remove(fieldIndex)}
												/>
											);
										})
									)}
								</div>
							</section>
						)}
					</div>

					<div className="flex items-center justify-end gap-2 pt-2 border-t">
						<Button
							type="button"
							variant="outline"
							onClick={() => modalStore.close()}
							disabled={updateApi.loading}
						>
							<X className="h-4 w-4 mr-1" />
							Cancel
						</Button>
						<Button type="submit" disabled={updateApi.loading}>
							{updateApi.loading ? "Saving…" : "Save changes"}
						</Button>
					</div>
				</form>
			</Form>
		</>
	);
};

export const EditStructurePropertyModal = observer(EditStructurePropertyModalComponent);

(EditStructurePropertyModal as { dialogClassName?: string }).dialogClassName =
	"max-w-5xl w-[min(64rem,calc(100vw-2rem))]";
